import { defineComponent as _defineComponent } from 'vue'
import {
  onBeforeMount,
  onServerPrefetch,
  useNuxtApp,
  useRouter,
  useState,
} from '#imports'
import { computed, ref, watch } from 'vue'
import { routeToFilters } from '~/apps/pu-links/infrastructure/routeToFilters'
import { pathSanitizer } from '~/utility/path'
import { FacetCount } from '~~/apps/facets/domain/FacetCounts'
import { getSearchCountService } from '~~/apps/search/infrastructure/SearchCountsService'
import { getContextInitialAppMenuLocationsMachine } from './machine'
import { appMenuLocationsMachineFactory } from './machineFactory'
import { LocationNode } from './types'


export default /*#__PURE__*/_defineComponent({
  __name: 'AppMenuLocations',
  props: {
    isOpen: { type: Boolean }
  },
  emits: ["can-scroll-to", "locationLoading", "location-url-clicked"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'can-scroll-to'): void
  (e: 'locationLoading', status: boolean): void
  (e: 'location-url-clicked'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  isOpen: boolean
};

const router = useRouter()
const { $i18n, $route } = useNuxtApp()
const sanitizePath = pathSanitizer({
  prependSlash: false,
  appendSlash: true,
  toLowerCase: false,
})




const stateStr = useState<string | undefined>('stateStr', () => undefined)
const stateLocations = ref<LocationNode[]>([])
const stateHierarchyPath = ref<string | undefined>(undefined)
const bookableCount = useState('bookableCount', () => 0)
const hierarchyCounts = useState<FacetCount>('hierarchyCounts', () => ({}))
const hierarchyPath = ref<string>(routeToFilters($route).hierarchyPath)
const machine = createMachine(hierarchyPath.value)

watch(
  () => props.isOpen,
  (isOpen) => {
    if (!isOpen) return
    const currentHierarchyPath =
      routeToFilters(router.currentRoute)?.hierarchyPath || ''
    emit('locationLoading', true)
    hierarchyPath.value = currentHierarchyPath
    updateLocation(currentHierarchyPath)
  },
)

onServerPrefetch(async () => {
  const countsService = getSearchCountService()
  const [countsServiceResult] = await Promise.all([
    countsService.fetchNonPriceSearchCounts($route.params.lang),
    setUpMachine(machine),
  ])
  bookableCount.value = countsServiceResult.bookableCount
  hierarchyCounts.value = countsServiceResult.hierarchyCounts
})

// onBeforeMount makes it possible to set stateLocations and stateHierarchyPath as normal refs without trasferring them from SSR to client
onBeforeMount(async () => {
  await setUpMachine(machine)
})

function createMachine(hierarchyPath?: string) {
  return appMenuLocationsMachineFactory({
    ...getContextInitialAppMenuLocationsMachine(),
    currentHierarchyPath: sanitizePath(hierarchyPath) || '/',
    langCode: $route.params.lang || 'en-gb',
  })
}

function setUpMachine(
  machine: ReturnType<typeof appMenuLocationsMachineFactory>,
) {
  return new Promise((resolve) => {
    machine.onTransition((newState) => {
      if (process.server) {
        stateStr.value = JSON.stringify(newState)
      } else {
        emit('locationLoading', newState.matches('loading'))
      }
      if (newState.matches('showing')) {
        stateLocations.value = newState.context.locations
        stateHierarchyPath.value = newState.context.currentHierarchyPath
        resolve(machine)
        emit('can-scroll-to')
      }
    })
    machine.start(stateStr.value ? JSON.parse(stateStr.value) : undefined)
  })
}

const locations = computed(() => {
  const locations =
    stateLocations.value.map((location) => {
      return setCountsForLocation(location)
    }) || []
  return [getAllLocationsNode(), ...locations]
})

const currentHierarchyPath = computed(() => {
  return stateHierarchyPath.value || ''
})

function setCountsForLocation(location: LocationNode) {
  return {
    ...location,
    children: location.children
      ? location.children.map((l) => setCountsForLocation(l))
      : [],
    count: hierarchyCounts.value[location.path]
      ? hierarchyCounts.value[location.path]
      : 0,
  }
}

function getAllLocationsNode(): LocationNode {
  return {
    children: [],
    count: bookableCount.value,
    label: $i18n.t('All locations') as string,
    parentPath: '',
    path: '',
    type: -1,
  }
}

function updateLocation(path: string) {
  machine.send('CHANGE_HIERARCHY', { path })
}

function nodeUrlClicked(path: string) {
  emit('location-url-clicked')
  hierarchyPath.value = path
}

return { __sfc: true,router, $i18n, $route, sanitizePath, props, emit, stateStr, stateLocations, stateHierarchyPath, bookableCount, hierarchyCounts, hierarchyPath, machine, createMachine, setUpMachine, locations, currentHierarchyPath, setCountsForLocation, getAllLocationsNode, updateLocation, nodeUrlClicked }
}

})